import { useState, useEffect, useCallback } from 'react';
import { getSettings, setSettings } from '../lib/storage';
import { getServerSettings } from '../lib/api';
import { track } from '../lib/telemetry';
import type { Settings, Mode } from '../lib/types';

const DEFAULT_SETTINGS: Settings = {
  buyOfferPercent: 60,
  defaultMode: 'sell',
};

export function useSettings() {
  const [settings, setLocalSettings] = useState<Settings>(DEFAULT_SETTINGS);
  const [isLoading, setIsLoading] = useState(true);

  // Load settings on mount
  useEffect(() => {
    async function loadSettings() {
      try {
        // First load from local storage
        const localSettings = await getSettings();
        setLocalSettings(localSettings);

        // Then try to sync from server
        try {
          const serverSettings = await getServerSettings();
          setLocalSettings(serverSettings);
          await setSettings(serverSettings);
        } catch (error) {
          // Server settings fetch failed, use local
          console.warn('Failed to fetch server settings:', error);
        }
      } catch (error) {
        console.error('Settings load error:', error);
      } finally {
        setIsLoading(false);
      }
    }

    loadSettings();
  }, []);

  const updateBuyOfferPercent = useCallback(async (percent: number) => {
    const newSettings = { ...settings, buyOfferPercent: percent };
    setLocalSettings(newSettings);
    await setSettings(newSettings);
    track('buy_preset_used', { percent });
  }, [settings]);

  const updateDefaultMode = useCallback(async (mode: Mode) => {
    const newSettings = { ...settings, defaultMode: mode };
    setLocalSettings(newSettings);
    await setSettings(newSettings);
  }, [settings]);

  return {
    settings,
    isLoading,
    updateBuyOfferPercent,
    updateDefaultMode,
  };
}
